// src/pages/AuthCallback.tsx
import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * AuthCallback page - Repurposed for invitation acceptance redirect
 * 
 * This page can be used to handle invitation token redirects if needed.
 * For now, it redirects to registration page if a token is present,
 * otherwise redirects to login.
 */
export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const error = searchParams.get('error');
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // If there's an error (like no_role_assigned), handle it
    if (error === 'no_role_assigned') {
      setIsLoading(false);
      return;
    }

    // If there's a token, redirect to registration page
    if (token) {
      const registrationUrl = email 
        ? `/register?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`
        : `/register?token=${encodeURIComponent(token)}`;
      setRedirectPath(registrationUrl);
      setIsLoading(false);
      return;
    }

    // Otherwise, try to refresh user and redirect
    refreshUser()
      .then(() => {
        // Check if there's a stored redirect path, otherwise go to /main
        const storedPath = sessionStorage.getItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAfterLogin');
        const redirectTo = (storedPath && storedPath !== '/') ? storedPath : '/main';
        setRedirectPath(redirectTo);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error refreshing user:', err);
        // Redirect to login if refresh fails
        setRedirectPath('/login');
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, token, email]);

  if (error === 'no_role_assigned') {
    return <Navigate to="/no-role" replace />;
  }

  // Redirect once we have the path
  if (redirectPath && !isLoading) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
      <CircularProgress />
      <Typography>Processing...</Typography>
    </Box>
  );
}

